import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { deepLinkService } from '../../services/deepLinkService';

/**
 * DeepLinkHandler Component
 * Initializes deep link handling and listens for incoming deep links
 */
const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();
  const isInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }

    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Deep linking only available on native platforms');
      return;
    }

    let cleanupListener: (() => void) | null = null;

    const initializeDeepLinking = async () => {
      try {
        // Initialize the deep link service with navigation
        deepLinkService.initialize(navigate);
        isInitialized.current = true;

        //console.log(`Initializing deep linking on ${Capacitor.getPlatform()} platform`);

        // Handle initial deep link (when app is launched from a link)
        const handleInitialLink = async () => {
          try {
            const result = await App.getLaunchUrl();
            if (result?.url) {
             // console.log('App launched with URL:', result.url);
              // Add a small delay for Android to ensure the app is fully initialized
              if (Capacitor.getPlatform() === 'android') {
                setTimeout(async () => {
                  await deepLinkService.handleDeepLink(result.url);
                }, 500);
              } else {
                await deepLinkService.handleDeepLink(result.url);
              }
            } else {
              console.log('No launch URL found');
            }
          } catch (error) {
            console.error('Error getting launch URL:', error);
          }
        };

        // Handle deep links when app is already running
        const handleAppUrlOpen = async (event: URLOpenListenerEvent) => {
          console.log(`App URL open event on ${Capacitor.getPlatform()}:`, event);
          if (event?.url) {
            try {
              // For Android, ensure we handle the deep link after a brief delay to allow for any ongoing navigation
              if (Capacitor.getPlatform() === 'android') {
                setTimeout(async () => {
                  await deepLinkService.handleDeepLink(event.url);
                }, 100);
              } else {
                await deepLinkService.handleDeepLink(event.url);
              }
            } catch (error) {
              console.error('Error handling deep link:', error);
            }
          }
        };

        // Set up listeners
        await handleInitialLink();

        const listenerPromise = App.addListener('appUrlOpen', handleAppUrlOpen);
        const listener = await listenerPromise;
        
        cleanupListener = () => {
          listener.remove();
        };

      } catch (error) {
        console.error('Error initializing deep linking:', error);
      }
    };

    // Initialize with a small delay to ensure React context is ready
    const timeoutId = setTimeout(() => {
      initializeDeepLinking();
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (cleanupListener) {
        cleanupListener();
      }
    };
  }, [navigate]);

  // This component doesn't render anything
  return null;
};

export default DeepLinkHandler;
